import type { Member, Role, Organisation, Project } from "./types";
import type { Permission } from "@/lib/rbac/permissions";
import { API_MODE, delay, notImplemented } from "./helpers";
import * as db from "@/lib/mocks/db";
import { getIdentity, request, NotSentError, mockRefusal, SavedNotShownError } from "./http";
import { readAllPages } from "./read-all";
import {
  paths,
  type WireOrganisation,
  type WireProject,
  type WirePage,
  type WireRole,
  type WireRoleGrant,
  type WireRoleAssignee,
} from "./wire";

/**
 * S-177, RULED 2026-08-27: "one organisation per login. A person working with
 * two customers holds two logins, one from each organisation's invitation."
 *
 * So a session carries ONE organisation, not a set. The register spells out
 * what that removes: "no set in the token, no active-org choice, no switcher".
 *
 * The RAD is UNAMENDED — RAD §8.2.4 still reads "many-to-many via membership"
 * while RAD §8.3's ER diagram draws one-to-many. This follows the sponsor's
 * ruling; the amendment is owed through change control.
 */
export async function getSession(): Promise<{ user: Member; organisation: Organisation; projects: Project[] }> {
  if (API_MODE === "http") {
    const identity = getIdentity();
    if (!identity) throw new Error("getSession called before anyone is signed in");

    // 2.668: GET /v1/projects pages at 50 (tenancy.ListPageSize) and was read
    // once, so an organisation's 51st project was missing from the switcher, the
    // capability grid and every project-scoped screen, with nothing saying so.
    const [orgs, projects] = await Promise.all([
      request<{ items: WireOrganisation[] }>(paths.organisations()),
      readAllPages<WireProject>(
        (cursor) => request<WirePage<WireProject>>(paths.projects(), { query: { cursor } }),
        "projects",
      ),
    ]);

    // S-177: one organisation per login. RAD §5.2 spells this route "List
    // organisations (Platform Admin) / own org (Org Admin)", so a normal user
    // sees exactly one and the first IS theirs.
    const org = orgs.items?.[0];
    if (!org) throw new Error("this account belongs to no organisation");

    return {
      user: {
        id: identity.sub,
        // The token carries a display name, not a split one. Rather than guess
        // at a surname by splitting on a space — which is wrong for a great
        // many names — the whole thing goes in `firstName` and `lastName` stays
        // empty. Every rendering site joins the two.
        firstName: identity.name ?? identity.email ?? "You",
        lastName: "",
        email: identity.email ?? "",
        enabled: true,
        // Memberships are a role-assignment concern (GET /v1/roles), not part
        // of the session. Empty rather than guessed: a fabricated membership
        // would drive the console's permission checks off fiction.
        memberships: [],
      },
      organisation: {
        id: org.organisation_id,
        name: org.name,
        plan: (org.plan_tier as Organisation["plan"]) ?? "Trial",
      },
      // `orgId` is the signed-in organisation because there is exactly one.
      // The platform keys a project to its WORKSPACE — see WireProject.
      projects: projects.map((p) => ({
        id: p.project_id,
        orgId: org.organisation_id,
        workspaceId: p.workspace_id,
        name: p.name,
      })),
    };
  }
  await delay(80);
  return { user: db.currentUser, organisation: db.organisations[0], projects: db.projects };
}

/**
 * Every role. The route pages at 50 (`role.ListPageSize`), and a first page read
 * as the whole list hid roles from the Roles page and made "What can they do?"
 * say a person held no role they held (2.663). Read to the end, bounded; see
 * `readAllPages`.
 */
export async function listRoles(): Promise<Role[]> {
  if (API_MODE === "http") {
    const roles = await readAllPages<WireRole>(
      (cursor) => request<WirePage<WireRole>>(paths.roles(), { query: { cursor } }),
      "roles",
    );
    return roles.map(roleOf);
  }
  await delay(100);
  return db.roles;
}

/**
 * The platform's role, as this console's `Role`.
 *
 * `permissions: []` is a SHAPE, not a claim. The mock `Role` carries its powers
 * inline; a platform role's powers are grants in the authorization graph, read
 * by `listRoleGrants`. Nothing may render this empty array as "no permissions" —
 * that is the false claim of absence S-395 exists to remove, and the Roles page
 * reads grants per role instead.
 *
 * `builtIn: false` because every role this route returns is one an Org Admin
 * DEFINED. The two predefined roles of RAD 2.5 are relations in the model, not
 * rows in `app.role`, so they never appear here.
 */
function roleOf(w: WireRole): Role {
  return {
    id: w.role_id,
    name: w.name,
    description: w.description ?? "",
    builtIn: false,
    permissions: [],
  };
}

/**
 * What a role confers today (S-395).
 *
 * The distinction this call exists to preserve: an EMPTY array means the role
 * grants nothing, and a THROW means the platform could not say. A caller that
 * renders both as an empty list tells an administrator a role is harmless when
 * the platform has no idea — so this never swallows an error into `[]`.
 */
/**
 * ADR-096 (S-610): one grant a role holds, at PROJECT or WORKSPACE scope.
 *
 * A workspace grant is ONE tuple covering every project in the workspace,
 * including projects created after it, and carries only the four `can_*`
 * capabilities. The listing returns it once under its workspace, never expanded
 * into projects. `scope` is kept as sent: a value this build does not know is
 * shown by the grid as it is, never dropped (a role's authority is never
 * under-reported).
 */
export interface HeldGrant {
  scope: string;
  project_id?: string;
  workspace_id?: string;
  capability: string;
}

/** The request body for a held grant: exactly one of project_id and workspace_id. */
function grantBody(g: HeldGrant): Record<string, string> {
  if (g.scope === "workspace" && g.workspace_id) return { workspace_id: g.workspace_id, capability: g.capability };
  if (g.scope === "project" && g.project_id) return { project_id: g.project_id, capability: g.capability };
  throw new NotSentError(`A grant at scope "${g.scope}" cannot be sent.`);
}

const sameGrant = (a: HeldGrant, b: HeldGrant) =>
  a.scope === b.scope && a.project_id === b.project_id && a.workspace_id === b.workspace_id && a.capability === b.capability;

export async function listRoleGrants(roleId: string): Promise<HeldGrant[]> {
  if (API_MODE === "http") {
    const page = await request<{ items: (WireRoleGrant & { scope?: string; workspace_id?: string })[] }>(paths.roleGrants(roleId));
    // A server from before ADR-096 sends no `scope`; its grants are all project grants.
    return (page.items ?? []).map((w) => ({
      scope: w.scope ?? (w.workspace_id ? "workspace" : "project"),
      ...(w.project_id ? { project_id: w.project_id } : {}),
      ...(w.workspace_id ? { workspace_id: w.workspace_id } : {}),
      capability: w.capability,
    }));
  }
  await delay(80);
  return [...(mockGrants.get(roleId) ?? [])];
}

/** The mock's grants per role — it used to answer `[]` and forget every grant. */
const mockGrants = new Map<string, HeldGrant[]>();

/** Tests only: the mock grant store is module state. */
export function resetMockGrants(seed: Record<string, HeldGrant[]> = {}) {
  mockGrants.clear();
  for (const [roleId, grants] of Object.entries(seed)) mockGrants.set(roleId, [...grants]);
}

/**
 * Who holds a role (S-468). Until this route existed, `assignMember` and
 * `unassignMember` below were served by the platform and called by NOTHING in
 * this console: a write whose effect no screen could show, so no screen was ever
 * built on it.
 *
 * It THROWS rather than returning `[]` when the platform cannot answer. The
 * caller must keep those apart — "nobody holds this role" and "we could not ask"
 * are opposite statements about who can act.
 *
 * The ids are NOT necessarily people. A key can hold a role (FR-09), so the
 * surface resolves against the roster and the API keys, and shows an unresolved
 * id as the id.
 */
export async function listRoleAssignees(roleId: string): Promise<WireRoleAssignee[]> {
  if (API_MODE === "http") {
    const page = await request<{ items: WireRoleAssignee[] }>(paths.roleAssignees(roleId));
    return page.items ?? [];
  }
  await delay(80);
  return [];
}

/** Give a role a capability on a project (FR-11). */
/** Give a role a capability at project or workspace scope (FR-11, ADR-096). */
export async function grantHeld(roleId: string, g: HeldGrant): Promise<void> {
  if (API_MODE === "http") {
    await request<void>(paths.roleGrants(roleId), { method: "POST", body: grantBody(g) });
    return;
  }
  await delay();
  grantBody(g);
  const held = mockGrants.get(roleId) ?? [];
  if (!held.some((h) => sameGrant(h, g))) mockGrants.set(roleId, [...held, { ...g }]);
}

/** Take one grant back, at the scope it was made. ONE tuple (FR-11). */
export async function revokeHeld(roleId: string, g: HeldGrant): Promise<void> {
  if (API_MODE === "http") {
    await request<void>(paths.roleGrants(roleId), { method: "DELETE", body: grantBody(g) });
    return;
  }
  await delay();
  mockGrants.set(roleId, (mockGrants.get(roleId) ?? []).filter((h) => !sameGrant(h, g)));
}

export async function grantCapability(roleId: string, projectId: string, capability: string): Promise<void> {
  if (API_MODE === "http") {
    await request<void>(paths.roleGrants(roleId), {
      method: "POST",
      body: { project_id: projectId, capability },
    });
    return;
  }
  await delay();
  const held = mockGrants.get(roleId) ?? [];
  if (!held.some((g) => g.scope === "project" && g.project_id === projectId && g.capability === capability)) {
    mockGrants.set(roleId, [...held, { scope: "project", project_id: projectId, capability }]);
  }
}

/** Take a capability back. ONE tuple, whatever the assignee count (FR-11). */
export async function revokeCapability(roleId: string, projectId: string, capability: string): Promise<void> {
  if (API_MODE === "http") {
    await request<void>(paths.roleGrants(roleId), {
      method: "DELETE",
      body: { project_id: projectId, capability },
    });
    return;
  }
  await delay();
  mockGrants.set(roleId, (mockGrants.get(roleId) ?? [])
    .filter((g) => !(g.scope === "project" && g.project_id === projectId && g.capability === capability)));
}

/**
 * Add a person to a role.
 *
 * REFUSALS FROM THIS CALL ARE WORTH SHOWING VERBATIM. The platform refuses a
 * person who is not a member of the organisation AND a person who accepted an
 * invitation but has never signed in — because membership reaches the
 * authorization graph on their first request (S-391, ADR-048 clause 5). Its
 * message says which; a caller that replaced it with "Forbidden" would send an
 * administrator hunting a permission problem that does not exist.
 */
export async function assignMember(roleId: string, userId: string): Promise<void> {
  if (API_MODE === "http") {
    await request<void>(paths.roleAssignees(roleId), { method: "POST", body: { user_id: userId } });
    return;
  }
  await delay();
}

/** Remove a person from a role. Deliberately NOT gated on membership: a
 * colleague who has left must still be removable (S-391). */
export async function unassignMember(roleId: string, userId: string): Promise<void> {
  if (API_MODE === "http") {
    await request<void>(paths.roleAssignees(roleId), { method: "DELETE", body: { user_id: userId } });
    return;
  }
  await delay();
}

/* ─────────────── Mutations ─────────────── */

const id = (p: string) => `${p}-${Date.now()}-${Math.floor(performance.now() % 1000)}`;

export interface NewUserInput {
  firstName: string; lastName: string; email: string;
  roleId: string; accessAllTargets: boolean; password: string;
}

export async function createMember(projectId: string, input: NewUserInput): Promise<Member> {
  if (API_MODE === "http") return notImplemented("createMember");
  await delay();
  const member: Member = {
    id: id("user"), firstName: input.firstName, lastName: input.lastName, email: input.email, enabled: true,
    memberships: [{ projectId, roleId: input.roleId, accessAllTargets: input.accessAllTargets }],
  };
  db.members.push(member);
  return member;
}

export async function setMemberEnabled(memberId: string, enabled: boolean): Promise<void> {
  if (API_MODE === "http") return notImplemented("setMemberEnabled");
  await delay(120);
  const m = db.members.find((x) => x.id === memberId);
  if (m) m.enabled = enabled;
}

export async function deleteMember(memberId: string): Promise<void> {
  if (API_MODE === "http") return notImplemented("deleteMember");
  await delay(120);
  const i = db.members.findIndex((x) => x.id === memberId);
  if (i >= 0) db.members.splice(i, 1);
}

export interface RoleInput { name: string; description: string; permissions: Permission[] }

export async function createRole(input: RoleInput): Promise<Role> {
  if (API_MODE === "http") {
    const made = await request<WireRole>(paths.roles(), {
      method: "POST",
      body: { name: input.name, description: input.description },
    });
    return roleOf(made);
  }
  await delay();
  const role: Role = { id: id("role"), builtIn: false, ...input };
  db.roles.push(role);
  return role;
}

/**
 * Whether a role's DESCRIPTION can be changed here. 2.791 (S-711): the platform
 * renames a role (`PATCH /v1/roles/{role_id}`, `RoleRename`) and nothing else —
 * the body is `name` alone, and a description, permissions or any other field
 * is refused with 400 rather than ignored. So over HTTP the editor offers the
 * name only: a description field whose only outcome is a refusal is a promise
 * the platform never made. The mock store keeps both.
 */
export function roleDescriptionEditable(): boolean {
  return API_MODE !== "http";
}

/**
 * Rename a role (2.791, S-711). Over HTTP this sends `{name}` and NOTHING else —
 * `RoleRename` is closed (`additionalProperties: false`) and the server refuses
 * a description or permissions with 400, so sending them would turn every rename
 * into a refusal. The role keeps its id, its grants and its holders.
 *
 * The 200 answer is the role as it now reads. It is parsed strictly at the
 * boundary (invariant 9): an answer without a string `role_id` and `name` is not
 * a role, and the rename — which the platform DID apply — is reported as saved
 * but not shown, never as a refusal.
 */
export async function updateRole(roleId: string, input: RoleInput): Promise<Role> {
  if (API_MODE === "http") {
    const answer = await request<unknown>(paths.role(roleId), {
      method: "PATCH",
      body: { name: input.name },
    });
    const role = parseRenamedRole(answer);
    if (!role) throw new SavedNotShownError("The new name");
    return roleOf(role);
  }
  await delay();
  const role = db.roles.find((r) => r.id === roleId);
  if (!role || role.builtIn) throw mockRefusal("INVALID_REQUEST", "Role not editable");
  Object.assign(role, input);
  return role;
}

/** `Role` from the rename's answer, or null when the answer is not one. */
function parseRenamedRole(raw: unknown): WireRole | null {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.role_id !== "string" || !r.role_id) return null;
  if (typeof r.name !== "string") return null;
  if (r.description !== undefined && typeof r.description !== "string") return null;
  if (typeof r.created_at !== "string" || typeof r.updated_at !== "string") return null;
  return {
    role_id: r.role_id,
    name: r.name,
    description: r.description as string | undefined,
    created_at: r.created_at,
    updated_at: r.updated_at,
  };
}

export async function deleteRole(roleId: string): Promise<void> {
  if (API_MODE === "http") {
    await request<void>(paths.role(roleId), { method: "DELETE" });
    return;
  }
  await delay(120);
  const i = db.roles.findIndex((r) => r.id === roleId && !r.builtIn);
  if (i >= 0) db.roles.splice(i, 1);
}
